/**
 * Panneau latéral modal : glisse depuis la droite par-dessus la page, qui reste
 * visible à gauche sous un voile.
 *
 * Sert à consulter un élément d'une liste sans quitter la liste (la fiche d'un
 * candidat depuis sa campagne) : tri, filtres et défilement de la page restent
 * en place derrière.
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "@repo/utils";
import { useFenetreModale } from "./use-fenetre-modale";

type Props = {
  ouvert: boolean;
  onFermer: () => void;
  /** Nom accessible du panneau. */
  libelle: string;
  /** Barre d'en-tête collante (navigation, fermeture). */
  entete?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

export function PanneauLateral({
  ouvert,
  onFermer,
  libelle,
  entete,
  children,
  className,
}: Props) {
  const panneau = useRef<HTMLDivElement>(null);
  const fermer = useRef(onFermer);

  useEffect(() => {
    fermer.current = onFermer;
  }, [onFermer]);

  useFenetreModale(ouvert, panneau, fermer);

  if (!ouvert) return null;

  return createPortal(
    <div className="fixed inset-0 z-40 flex justify-end bg-voile/30" onClick={onFermer}>
      <div
        ref={panneau}
        role="dialog"
        aria-modal="true"
        aria-label={libelle}
        tabIndex={-1}
        className={cn(
          "flex h-full w-full max-w-5xl flex-col bg-background shadow-2xl outline-none sm:w-[min(88vw,64rem)] motion-safe:animate-in motion-safe:slide-in-from-right",
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {entete && (
          <div className="sticky top-0 z-10 border-b bg-background px-6 py-3">
            {entete}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
